<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <login-layout>
    <oxd-text class="orangehrm-login-title" tag="h5">
      {{ $t('auth.login') }} Mobile
    </oxd-text>
    <div class="orangehrm-login-form">
      <div class="orangehrm-login-error">
        <oxd-alert
          :show="error !== null"
          :message="error?.message || ''"
          type="error"
        ></oxd-alert>
      </div>
      <oxd-form
        ref="loginForm"
        method="post"
        :action="submitUrl"
        @submit-valid="onSubmit"
      >
        <input name="_token" :value="token" type="hidden" />

        <oxd-form-row>
          <input
            v-model="username"
            class="username"
            name="phone"
            :label="$t('general.phone')"
            label-icon="person"
            :placeholder="$t('general.phone')"
            :rules="rules.username"
            autofocus
            type="number"
            pattern="[0-9]*"
          />
        </oxd-form-row>

        <oxd-form-row v-if="otpState === 1">
          <oxd-text class="user-form-header-text" tag="p">
            一次性密碼已經發送到 SMS
          </oxd-text>
          <div class="input-with-button">
            <oxd-input-field
              v-model="otp"
              name="otp"
              placeholder="一次性密碼"
            />
            <oxd-button
              type="button"
              @click="reSend"
              :label="reSendLabel"
              :disabled="otpTime != 0"
            ></oxd-button>
          </div>
        </oxd-form-row>

        <!-- Switch Button to Show/Hide Password Field -->
        <oxd-form-row class="user-form-header" v-if="otpState === 0">
          <oxd-text class="user-form-header-text" tag="p">
            使用密碼登入
          </oxd-text>
          <oxd-switch-input v-model="showPasswordField" />
        </oxd-form-row>

        <!-- Password Field, shown based on the switch state -->
        <oxd-form-row v-if="showPasswordField && otpState === 0">
          <oxd-input-field
            v-model="password"
            name="password"
            type="password"
            :label="$t('general.password')"
            :placeholder="$t('general.password')"
            :rules="rules.password"
          />
        </oxd-form-row>

        <oxd-form-actions class="orangehrm-login-action">
          <oxd-button
            class="orangehrm-login-button"
            display-type="main"
            :label="$t('auth.login')"
            type="submit"
          />
        </oxd-form-actions>
      </oxd-form>
      <template v-if="authenticators.length > 0">
        <oxd-divider class="orangehrm-login-seperator"></oxd-divider>
        <social-media-auth :authenticators="authenticators"></social-media-auth>
      </template>
    </div>
  </login-layout>
</template>

<script>
import {urlFor} from '@ohrm/core/util/helper/url';
import {OxdAlert, OxdIcon, OxdSheet, OxdSwitchInput} from '@ohrm/oxd';
import {required} from '@ohrm/core/util/validation/rules';
import {navigate, reloadPage} from '@ohrm/core/util/helper/navigation';
import LoginLayout from '@/orangehrmAuthenticationPlugin/components/LoginLayout.vue';
import SocialMediaAuth from '@/orangehrmAuthenticationPlugin/components/SocialMediaAuth.vue';
import {APIService} from '@/core/util/services/api.service';
import {AxiosResponse} from 'axios';

export default {
  components: {
    'oxd-alert': OxdAlert,
    'login-layout': LoginLayout,
    'social-media-auth': SocialMediaAuth,
    'oxd-switch-input': OxdSwitchInput,
  },

  props: {
    error: {
      type: Object,
      default: () => null,
    },
    token: {
      type: String,
      required: true,
    },
    showSocialMedia: {
      type: Boolean,
      default: true,
    },
    isDemoMode: {
      type: Boolean,
      default: false,
    },
    authenticators: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      username: '',
      password: '',
      otp: '',
      rules: {
        username: [required],
        password: [required],
      },
      submitted: false,
      showPasswordField: false,
      showOptField: true,
      showOTP: false,
      otpState: 0,
      otpMsg: '',
      otpTime: 0,
    };
  },

  computed: {
    submitUrl() {
      return urlFor('/auth/validatemobile');
    },
    reSendLabel() {
      if (this.otpTime == 0) {
        return '重發';
      }
      return '重發(' + this.otpTime + ')';
    },
  },

  beforeMount() {
    setInterval(() => {
      if (this.otpTime > 0) {
        this.otpTime--;
      }
    }, 1000);

    setTimeout(() => {
      reloadPage();
    }, 1200000); // 20 * 60 * 1000 (20 minutes);
  },

  methods: {
    onSubmit() {
      if (!this.submitted) {
        this.submitted = true;
        if (!this.username) {
          alert('請提供電話號碼');
          this.submitted = false;
        } else if (this.otpState === 1 && !this.otp) {
          alert('請提供一次性密碼');
        } else if (this.otpState === 1) {
          this.validOTP();
        } else if (this.otpState === 0 && this.showPasswordField) {
          this.$refs.loginForm.$el.submit();
        } else if (this.otpState === 0) {
          this.getOTP();
        }
      }
    },
    reSend() {
      this.getOTP();
    },
    navigateUrl() {
      navigate('/auth/requestPasswordResetCode');
    },
    async getOTP() {
      console.log('baseUrl', window.appGlobal.baseUrl);
      try {
        const http = new APIService(window.appGlobal.baseUrl);

        const response = await http.request({
          method: 'POST',
          url: '../api/LoginOTP.php',
          data: {
            number: this.username,
            action: 'getOTP',
          },
        });
        if (response.data.rscode === 3) {
          alert(response.data.msg);
        } else if (response.data.rscode === 1) {
          this.otpState = response.data.rscode;
        }
        this.submitted = false;
        this.otpMsg = response.data.msg;
        this.otpTime = 60;
        console.log('rscode', response.data.rscode);
      } catch (error) {
        console.log('error', error);
      }
    },
    async validOTP() {
      try {
        const http = new APIService(window.appGlobal.baseUrl);

        const response = await http.request({
          method: 'POST',
          url: '../api/LoginOTP.php',
          data: {
            number: this.username,
            otp: this.otp,
            action: 'validOTP',
          },
        });
        if (response.data.rscode === 3) {
          alert(response.data.msg);
        } else if (response.data.rscode === 1) {
          this.$refs.loginForm.$el.submit();
        }
        this.submitted = false;
        console.log('rscode', response.data.rscode);
      } catch (error) {
        console.log('error', error);
      }
    },
  },
};
</script>

<style src="./login.scss" lang="scss" scoped></style>
<style type="text/css">
input.username {
  box-sizing: border-box;
  color: #64728c;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  border-radius: 0.65rem;
  padding: 0.675rem 0.5rem;
  width: 100%;
  min-height: 45px;
  border: 1px solid #e8eaef;
}
.input-with-button {
  display: flex;
  align-items: center;
}
</style>
